<script>
    import {
        Button,
        Modal,
        ModalBody,
        ModalFooter,
        ModalHeader,
        Table
    } from "@sveltestrap/sveltestrap";
    import {onMount} from "svelte";
    import {eventBridge, portalConfiguration} from "../../stores.js";
    import Fa from "svelte-fa";
    import {faTrash} from "@fortawesome/free-solid-svg-icons";
    import ConfirmDeleteTokenModal from "./ConfirmDeleteTokenModal.svelte";
    import TokenDetailsModal from "./TokenDetailsModal.svelte";
    import ButtonLine from "../snippets/ButtonLine.svelte";
    import AddTokenModal from "./AddTokenModal.svelte";
    import Help from "../snippets/Help.svelte";
    import {handleStaticModalToggle, modalClassName, modalFade} from "./utils.js";

    let { showModal = $bindable(false) } = $props();

    let tokens = $state([]);
    let selectedToken = $state(null);

    let showTokenDetails = $state(false);
    let showConfirmDeleteToken = $state(false);
    let showAddToken = $state(false);
    let tokenRows = $state();
    let tokenRowIndex = $state();
    let csHasSentTokens = $state(false);
    const TOKEN_ROW_FOCUS_DELAY = 1000; // in millis
    let useTokenRowFocusDelay = $state(false);
    
    const toggle = () => (showModal = !showModal);
    const handleModalToggle = handleStaticModalToggle(toggle);

    function handleAvailableSecurityTokenList(event) {
        const payload = JSON.parse(event.payload);
        const tokensFromPayload = payload.securityTokenList;
        tokens = tokensFromPayload;
        csHasSentTokens = true;
        tokenRowIndex = undefined;
    }

    function registerTokenListEvents() {
        $eventBridge.addEventListener('cs:AvailableSecurityTokenList', handleAvailableSecurityTokenList);
        $eventBridge.dispatchEvent('cs:getAvailableSecurityTokenList');
        $eventBridge.dispatchEvent('cs:subscribeCardEvent');
    }

    function unregisterTokenListEvents() {
        $eventBridge.removeEventListener('cs:AvailableSecurityTokenList', handleAvailableSecurityTokenList);
    }

    onMount(() => {
        registerTokenListEvents();
        tokenRows = document.getElementsByClassName("token-administration-modal-token-row");

        return () => {
            unregisterTokenListEvents();
        };
    });

    $effect(() => {
        if (showModal && csHasSentTokens && tokenRows?.length === tokens.length) {
            if (!useTokenRowFocusDelay) {
                focusTableRows();
            } else {
                setTimeout(focusTableRows, TOKEN_ROW_FOCUS_DELAY);
                useTokenRowFocusDelay = false;
            }
            csHasSentTokens = false;
        }
    });
    function confirmDeleteToken(token) {
        selectedToken = token;
        showConfirmDeleteToken = true;
    }

    function openTokenDetails(token) {
        selectedToken = token;
        showTokenDetails = true;
    }

    function handleTokenListKeyboardOperations(event) {
        if (document.activeElement?.id === "token-administration-add-token") {
            if (event.key === "Enter") {
                document.getElementById("token-administration-add-token")?.click();
                return;
            }
        }
        
        if(!tokenRows || !Array.from(tokenRows).includes(document.activeElement)) {
            return;
        }

        if (event.key === "ArrowUp") {
            tokenRowIndex = (--tokenRowIndex + tokenRows.length) % tokenRows.length;
            processTokenRowIndexUpdate();
        }

        if (event.key === "ArrowDown") {
            tokenRowIndex = ++tokenRowIndex % tokenRows.length;
            processTokenRowIndexUpdate();
        }

        if (event.key === "Enter") {
            openTokenDetails(tokens[tokenRowIndex]);
        }

        if (event.key === "Delete") {
            tokenRows[tokenRowIndex].getElementsByClassName('delete-token')[0]?.click();
        }
    }

    function updateSelectedToken() {
        selectedToken = tokens[tokenRowIndex];
    }
    
    function focusSelectedTokenRow() {
        tokenRows[tokenRowIndex].focus();
    }
    
    function processTokenRowIndexUpdate() {
        updateSelectedToken();
        focusSelectedTokenRow();
    }
    
    function focusAddTokenButton() {
        if (!showModal) {
            return;
        }
        document.getElementById("token-administration-add-token")?.focus();
    }

    function focusTableRows() {
        if (tokenRows.length === 0) {
            focusAddTokenButton();
            return;
        }
        if (tokenRowIndex === undefined) {
            tokenRowIndex = 0;
        }
        processTokenRowIndexUpdate();
    }
</script>
<svelte:window onkeydown={handleTokenListKeyboardOperations}/>

<Modal
    id="token-administration-modal"
    isOpen={showModal}
    toggle={handleModalToggle}
    fade={$modalFade}
    modalClassName={$modalClassName}
    size="xl"
    scrollable
    on:open={focusTableRows}
>
    <ModalHeader toggle={handleModalToggle}>Zertifikate verwalten</ModalHeader>

    <ModalBody>
        <Table hover style="table-layout: fixed;">
            <thead>
                <tr>
                    <th style="width: 30px;"></th>
                    <th>Technische Bezeichnung</th>
                    <th style="width: 20%;">Bezeichnung in beA</th>
                    <th style="width: 15%;">Art</th>
                    <th style="width: 15%;">Ablaufdatum</th>
                </tr>
            </thead>

            <tbody>
                {#each tokens as token}
                    <tr onclick={() => {
                            tokenRowIndex = tokens.indexOf(token);
                            processTokenRowIndexUpdate();
                        }}
                        ondblclick={() => openTokenDetails(token)}
                        data-test-entity-id={token.fingerprint}
                        data-test-id="tokenAdministrationModal-dataTable-row-{token.fingerprint}"
                        class:unavailable={token.state === 'NOT_AVAILABLE'}
                        class:error={token.state.startsWith('ERROR')}
                        class:selected={token.fingerprint === selectedToken?.fingerprint}
                        class="token-administration-modal-token-row"
                        tabindex={token.fingerprint === selectedToken?.fingerprint ? '0' : '-1'}
                    >
                        {#if  token.type === 'SW'}
                            <td
                                style="text-align: center;"
                                onclick={() => confirmDeleteToken(token)}
                                title="Zertifikat löschen"
                                class="delete-token"
                                data-test-id="tokenAdministrationModal-dataTable-row-action-delete-{token.fingerprint}"
                            >
                                <Fa icon={faTrash}/>
                            </td>
                        {:else}
                            <td></td>
                        {/if}
                        <td class="ellipsis" style="max-width: 40%;">{token.customLabel}</td>
                        <td>{token.customName || ''}</td>
                        <td>{token.type === 'HW' ? 'Hardware-Zertifikat' : 'Software-Zertifikat'}</td>
                        <td>{token.expiryDate}</td>
                    </tr>
                {/each}
            </tbody>
        </Table>
    </ModalBody>

    <ModalFooter>
        <ButtonLine>
            {#snippet left()}
                <Help url={`${$portalConfiguration.help ?? ''}/einstellungen-in-ihrem-bea/profilverwaltung/sicherheits-token/hinzufuegen`} data-test-id="tokenAdministrationModal-help" />
            {/snippet}
            {#snippet middle()}
                <button type="button" id="token-administration-add-token" onclick={() => (showAddToken = true)} data-test-id="tokenAdministrationModal-newToken">+ Neues Software-Zertifikat hinzufügen</button>
            {/snippet}
            <Button color="primary" on:click={() => (showTokenDetails = true)} disabled={!selectedToken} data-test-id="tokenAdministrationModal-button-details">Details anzeigen</Button>
        </ButtonLine>
    </ModalFooter>
</Modal>

<TokenDetailsModal bind:showModal={showTokenDetails} token={selectedToken} />
<ConfirmDeleteTokenModal
    bind:showConfirm={showConfirmDeleteToken}
    token={selectedToken}
    tokenDeletion={() => {
        selectedToken = null;
        useTokenRowFocusDelay = true;
    }}
/>
<AddTokenModal bind:showModal={showAddToken} submit={() => (useTokenRowFocusDelay = true)} />

<style>
    thead {
        background-color: var(--brak-dark-blue);
        color: var(--brak-greyed-white);
    }

    tbody {
        cursor: pointer;
        user-select: none;
    }

    #token-administration-add-token {
        text-decoration: none;
        font-style: italic;
        font-weight: bold;
        color: var(--brak-dark-blue);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
    }

    .delete-token > :global(*) {
        visibility: hidden;
    }

    tr:hover .delete-token > :global(*) {
        visibility: inherit;
    }

    .unavailable {
        background-color: darkgrey;
    }

    .error {
        background-color: var(--brak-red);
    }

    .selected {
        background-color: var(--brak-medium-blue);
    }
</style>
